import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { gql } from "@apollo/client";
import type {
  QueryPullInvoiceArgs,
  SyncableInvoiceInput,
  SyncableLineItemInput,
  SyncablePayerInput,
  SyncableTransactionInput,
} from "elephant-web/gql";
import { useMemo } from "react";
import { useMatch } from "react-router-dom";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { Invoice } from "../collections/invoice";
import type { RxDBCollections } from "../provider";
import {
  SyncInvoicePullDocument,
  SyncInvoicePushDocument,
  type WholeSyncableInvoiceFragment,
} from "./__generated__/invoice.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicateInvoice = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<Invoice>;
}) => {
  const query = useQuerystring();
  const matchPat = useMatch("/dev-patient/:patientId/*");
  const patientId = matchPat?.params.patientId ?? query.patientUuid;
  const [featureFlagEnabled] = useFeatureFlag(featureFlags.billing.rxdbBilling);

  const push = useMemo(
    () => ({
      mutation: SyncInvoicePushDocument,
      mapRxToGql: mapRxInvoiceToGqlInvoiceInput,
    }),
    [],
  );

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableInvoiceFragment> | undefined
  >(() => {
    return featureFlagEnabled && patientId
      ? {
          query: SyncInvoicePullDocument,
          reSyncInterval: { minutes: 5 },
          ...createPersistedCheckpointPullBuilders<
            QueryPullInvoiceArgs,
            WholeSyncableInvoiceFragment
          >({
            buildInputVariables: (checkpoint, batchSize) => ({
              input: {
                limit: batchSize,
                filters: { patientId },
                checkpoint,
              },
            }),
            buildPullCheckpoint: (doc) => ({
              id: doc.id,
              updatedAt: doc.meta.updatedAt,
            }),
            entityId: patientId,
            db,
            collectionName: collection?.name,
          }),
        }
      : undefined;
  }, [featureFlagEnabled, patientId, db, collection]);

  return useGqlReplication<
    Invoice,
    WholeSyncableInvoiceFragment,
    SyncableInvoiceInput
  >({
    db,
    collection,
    push: featureFlagEnabled && patientId ? push : undefined,
    pull,
    mapGqlToRx: mapGqlInvoiceToRxInvoice,
    batchSize: 100,
    expireData: true,
    metadataLocalStorageKeySuffix: `invoice-${patientId}`,
    enabled: featureFlagEnabled && !!patientId,
  });
};

const stripBalance = (
  b: WholeSyncableInvoiceFragment["balance"],
): Invoice["balance"] => ({
  currency: b.currency,
  total: b.total,
  due: b.due ?? null,
  paid: b.paid ?? null,
  exempt: b.exempt ?? null,
  waived: b.waived ?? null,
});

const stripInpatientDetails = (
  d:
    | WholeSyncableInvoiceFragment["inpatientDetails"]
    | Invoice["inpatientDetails"],
): Invoice["inpatientDetails"] =>
  d
    ? {
        admission: d.admission ?? null,
        discharge: d.discharge ?? null,
        ward: d.ward ?? null,
        inpatientNumber: d.inpatientNumber ?? null,
        dailyRebateAmount: d.dailyRebateAmount ?? null,
      }
    : null;

const stripLineItemsForPull = (
  items: WholeSyncableInvoiceFragment["lineItems"],
): Invoice["lineItems"] =>
  items.map((i) => ({
    id: i.id,
    cost: i.cost,
    quantity: i.quantity ?? null,
    priceListItemId: i.priceListItemId ?? null,
    revenueDepartmentId: i.revenueDepartmentId ?? null,
    tariffId: i.tariffId ?? null,
    incurredAt: i.incurredAt ?? null,
    idempotencyKey: i.idempotencyKey ?? null,
    automated: i.automated ?? null,
    disabled: i.disabled ?? null,
    missingPrice: i.missingPrice ?? null,
    supplier: i.supplier ?? null,
    actorId: i.actorId ?? null,
    externalReference: i.externalReference
      ? {
          origin: i.externalReference.origin,
          foreignId: i.externalReference.foreignId,
        }
      : null,
  }));

const stripLineItemsForPush = (
  items: Invoice["lineItems"],
): SyncableLineItemInput[] =>
  items.map((i) => {
    return {
      id: i.id,
      cost: i.cost,
      quantity: i.quantity ?? null,
      priceListItemId: i.priceListItemId ?? null,
      revenueDepartmentId: i.revenueDepartmentId ?? null,
      tariffId: i.tariffId ?? null,
      incurredAt: i.incurredAt ?? null,
      idempotencyKey: i.idempotencyKey ?? null,
      automated: i.automated ?? null,
      disabled: i.disabled ?? null,
      missingPrice: i.missingPrice ?? null,
      supplier: i.supplier ?? null,
      actorId: i.actorId ?? null,
      externalReference: i.externalReference
        ? {
            origin: i.externalReference.origin,
            foreignId: i.externalReference.foreignId,
          }
        : null,
    };
  });

const stripPayers = (
  payers: WholeSyncableInvoiceFragment["payers"],
): Invoice["payers"] =>
  payers.map((p) => ({
    id: p.id,
    name: p.name,
    type: p.type,
    balance: stripBalance(p.balance),
    claimedLineItems: p.claimedLineItems.map((item) => ({
      lineItemId: item.lineItemId,
      amountInPence: item.amountInPence,
    })),
    insuranceDetails: p.insuranceDetails
      ? {
          name: p.insuranceDetails.name ?? null,
          insuranceNumber: p.insuranceDetails.insuranceNumber ?? null,
          dependent: p.insuranceDetails.dependent ?? null,
        }
      : null,
  }));

const stripTransactionsForPull = (
  txs: WholeSyncableInvoiceFragment["transactions"],
): Invoice["transactions"] =>
  txs.map((t) => ({
    id: t.id,
    uuid: t.uuid ?? null,
    type: t.type,
    status: t.status,
    timestamp: t.timestamp,
    actionedByUserId: t.actionedByUserId ?? null,
    amount: t.amount
      ? { currency: t.amount.currency, value: t.amount.value }
      : null,
    paymentMethod: t.paymentMethod ?? null,
    paymentAuthCode: t.paymentAuthCode ?? null,
    exemptionType: t.exemptionType ?? null,
    notes: t.notes ?? null,
    payerName: t.payerName ?? null,
    payerType: t.payerType ?? null,
    refundReason: t.refundReason ?? null,
    refundPaymentMethod: t.refundPaymentMethod ?? null,
    refundAuthCode: t.refundAuthCode ?? null,
  }));

const stripTransactionsForPush = (
  txs: Invoice["transactions"],
): SyncableTransactionInput[] =>
  txs.map((t) => ({
    id: t.id,
    uuid: t.uuid ?? null,
    type: t.type,
    status: t.status,
    timestamp: t.timestamp,
    actionedByUserId: t.actionedByUserId ?? null,
    amount: t.amount
      ? { currency: t.amount.currency, value: t.amount.value }
      : null,
    paymentMethod: t.paymentMethod ?? null,
    paymentAuthCode: t.paymentAuthCode ?? null,
    exemptionType: t.exemptionType ?? null,
    notes: t.notes ?? null,
    payerName: t.payerName ?? null,
    payerType: t.payerType ?? null,
    refundReason: t.refundReason ?? null,
    refundPaymentMethod: t.refundPaymentMethod ?? null,
    refundAuthCode: t.refundAuthCode ?? null,
  }));

export const mapGqlInvoiceToRxInvoice = (
  doc: WholeSyncableInvoiceFragment,
): WithDeleted<Invoice> => {
  return {
    id: doc.id,
    facilityId: doc.facilityId,
    patientId: doc.patientId,
    patientType: doc.patientType,
    invoiceNumber: doc.invoiceNumber,
    invoiceStatus: doc.invoiceStatus,
    balance: stripBalance(doc.balance),
    inpatientDetails: stripInpatientDetails(doc.inpatientDetails),
    lineItems: stripLineItemsForPull(doc.lineItems),
    payers: stripPayers(doc.payers),
    transactions: stripTransactionsForPull(doc.transactions),
    meta: {
      createdAt: doc.meta.createdAt,
      updatedAt: doc.meta.updatedAt,
      createdByUserId: doc.meta.createdByUserId,
      updatedByUserId: doc.meta.updatedByUserId,
    },
    _deleted: doc.meta.deleted,
    finalizedAt: doc.finalizedAt ?? null,
    finalizedByUserId: doc.finalizedByUserId ?? null,
    priceListLastSyncedAt: doc.priceListLastSyncedAt ?? null,
  };
};

export const mapRxInvoiceToGqlInvoiceInput = (
  doc: WithDeleted<Invoice>,
): SyncableInvoiceInput => ({
  id: doc.id,
  facilityId: doc.facilityId,
  patientId: doc.patientId,
  patientType: doc.patientType,
  invoiceNumber: doc.invoiceNumber,
  invoiceStatus: doc.invoiceStatus,
  balance: stripBalance(doc.balance),
  inpatientDetails: stripInpatientDetails(doc.inpatientDetails),
  lineItems: stripLineItemsForPush(doc.lineItems),
  payers: doc.payers.map(
    (p): SyncablePayerInput => ({
      id: p.id,
      name: p.name,
      type: p.type,
      balance: stripBalance(p.balance),
      claimedLineItems: (p.claimedLineItems ?? []).map((item) => ({
        lineItemId: item.lineItemId,
        amountInPence: item.amountInPence,
      })),
      insuranceDetails: p.insuranceDetails
        ? {
            name: p.insuranceDetails.name ?? null,
            insuranceNumber: p.insuranceDetails.insuranceNumber ?? null,
            dependent: p.insuranceDetails.dependent ?? null,
          }
        : null,
    }),
  ),
  transactions: stripTransactionsForPush(doc.transactions),
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
    deleted: doc._deleted,
  },
  finalizedAt: doc.finalizedAt ?? null,
  finalizedByUserId: doc.finalizedByUserId ?? null,
  priceListLastSyncedAt: doc.priceListLastSyncedAt ?? null,
});

gql`
  fragment WholeSyncableInvoice on SyncableInvoice {
    id
    facilityId
    patientId
    patientType
    invoiceNumber
    invoiceStatus
    balance {
      currency
      total
      due
      paid
      exempt
      waived
    }
    inpatientDetails {
      admission
      discharge
      ward
      inpatientNumber
      dailyRebateAmount
    }
    lineItems {
      id
      cost
      quantity
      priceListItemId
      revenueDepartmentId
      tariffId
      incurredAt
      idempotencyKey
      automated
      disabled
      missingPrice
      supplier
      actorId
      externalReference {
        origin
        foreignId
      }
    }
    payers {
      id
      name
      type
      balance {
        currency
        total
        due
        paid
        exempt
        waived
      }
      claimedLineItems {
        lineItemId
        amountInPence
      }
      insuranceDetails {
        name
        insuranceNumber
        dependent
      }
    }
    transactions {
      id
      uuid
      type
      status
      timestamp
      actionedByUserId
      amount {
        currency
        value
      }
      paymentMethod
      paymentAuthCode
      exemptionType
      notes
      payerName
      payerType
      refundReason
      refundPaymentMethod
      refundAuthCode
      payerName
      payerType
    }
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
    finalizedAt
    finalizedByUserId
    priceListLastSyncedAt
  }
`;

gql`
  query SyncInvoicePull($input: PullInvoiceInput!) {
    pullInvoice(input: $input) {
      documents {
        ...WholeSyncableInvoice
      }
    }
  }
`;

gql`
  mutation SyncInvoicePush($input: PushInvoiceInput!) {
    pushInvoice(input: $input) {
      rows {
        ...WholeSyncableInvoice
      }
    }
  }
`;
